/**
 * Phase 8 — Project intake gate.
 *
 * Replaces the prior auto-create-and-redirect flow. The customer must
 * supply address + phone (or reuse a prior project's address) before
 * the chat workspace renders.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { listEstimates } from "@starz/shared/api/sdk.gen";
import type { Estimate } from "@starz/shared/api/types.gen";
import { useAuthUser } from "@/lib/auth/useAuth";
import { NewProjectModal } from "@/components/intake/NewProjectModal";
import { FormError } from "@/components/FormError";

export function NewEstimate() {
  const navigate = useNavigate();
  const user = useAuthUser();
  const [priorEstimates, setPriorEstimates] = useState<Estimate[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await listEstimates({ limit: 50 });
      if (cancelled) return;
      if ("error" in result) {
        setLoadError(result.error.error.message);
        return;
      }
      setPriorEstimates(result.data);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loadError) {
    return (
      <div className="mx-auto max-w-content px-12 py-16">
        <h1 className="mb-4 font-serif text-3xl text-starz-navy">
          We couldn't start your estimate.
        </h1>
        <FormError message={loadError} />
      </div>
    );
  }

  if (priorEstimates === null) {
    return (
      <div className="mx-auto max-w-content px-12 py-16">
        <p className="text-warm-gray">Loading…</p>
      </div>
    );
  }

  return (
    <NewProjectModal
      priorEstimates={priorEstimates}
      initialPhone={user?.phone ?? null}
      onCreated={(estimate) =>
        navigate(`/estimates/${estimate.estimate_id}`, { replace: true })
      }
      onCancel={() => navigate("/estimates", { replace: true })}
    />
  );
}
